import React, { useEffect, useRef } from 'react';
import { PropTypes } from 'prop-types';
import HeaderIconTooltip from '../HeaderIconTooltip/HeaderIconTooltip';

import {
  TextInput, Number, Multiselect, Heading, DateSelectorV2, TextArea,
} from './index';
import CustomSelect, { DEFAULT_SELECT_CLEAR_OPTION_LABEL } from './CustomSelect/CustomSelect';
import { formatImportDate } from '../../utils/dates';

// Must be less than the reset delay in useCustomFieldErrorScroll (200ms)
// to ensure the scroll fires before the flag resets to false.
const SCROLL_TO_ERROR_DELAY_MS = 100;
// Extra width (in ch) to account for padding and dropdown icon
const SELECT_EXTRA_WIDTH_CH = 4;

const DEFAULT_CUSTOM_FIELDS = [{}];

const CustomFieldsForm = ({
  customFields = DEFAULT_CUSTOM_FIELDS,
  setData = () => {},
  isLabelBeside = false,
  isViewOnly = false,
  iconSize = undefined,
  labelFontClassName = undefined,
  border = 'border-b border-vamos-blue-gray pb-2',
  headerBottomMargin = undefined,
  margins = 'mb-4 mt-0',
  paddingBottom = undefined,
  isHideHeader = false,
  isSingleColForContacts = false,
  inputFieldsHeight,
  inputHeightSelect,
  iconRight,
  iconBottom,
  errorMessage,
  errorMessageClass,
  className,
  setCustomFieldsChanged = () => {},
  shouldScrollToError = false,
  customFieldMaxWidths = {},
  isNumberFieldNegativeAllowed = true,
  clearFieldError,
}) => {
  const fieldRefs = useRef({});

  // Scroll to custom fields section if there are custom field errors
  useEffect(() => {
    if (!shouldScrollToError) return undefined;

    const scrollToFirstError = () => {
      if (!customFields || !errorMessage) return;

      const firstErrorField = customFields.find(cf => errorMessage[`custom_fields.${cf.label}`]);

      if (firstErrorField) {
        const el = fieldRefs.current[firstErrorField.key];
        el?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    };

    const timeoutId = setTimeout(scrollToFirstError, SCROLL_TO_ERROR_DELAY_MS);
    return () => clearTimeout(timeoutId);
  }, [shouldScrollToError, errorMessage, customFields]);

  const getFieldValue = (input) => {
    let fieldValue = 'N/A';
    switch (input.type) {
      case 'checkbox-group':
        if (input.values && input.values.length > 0) {
          fieldValue = input.values
            .filter((value) => value.selected)
            .map((value) => value.label)
            .join(', ') || 'N/A';
        }
        break;
      case 'select':
        fieldValue = input.values.find((value) => value.selected === true)?.value || 'N/A';
        break;
      case 'header':
        fieldValue = '';
        break;
      case 'date':
        // after changes on backend custom fields date saves as date 2026-04-03
        // previously it saved as string and from veb app it sended as 04/09/2026
        // need to display data in the format MM/DD/YYYY
        if (!input.value || input.value === '') {
          fieldValue = 'N/A';
        } else if (input.value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)) {
          fieldValue = input.value;
        } else if (input.value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/)) {
          fieldValue = formatImportDate(input.value);
        }
        break;
      default:
        fieldValue = input.value || 'N/A';
    }
    return fieldValue;
  };

  // Ensure each custom select is wide enough for either the default placeholder text or its longest option label, whichever is longer.
  // Extra width accounts for input padding and the dropdown icon.
  const getComputedSelectCustomWidth = (input) => {
    const placeholderLength = DEFAULT_SELECT_CLEAR_OPTION_LABEL.length;

    const longestOptionLength = input.values?.reduce(
      (max, option) => Math.max(max, (option.label || option.value || '').length),
      0,
    ) || 0;

    const requiredLength = Math.max(placeholderLength, longestOptionLength);

    return `${requiredLength + SELECT_EXTRA_WIDTH_CH}ch`;
  };

  const wrapWithRef = (input, children, wrapperClassName = 'mb-4') => (
    <div
      className={wrapperClassName}
      ref={(el) => {
        if (el) fieldRefs.current[input.key] = el;
        else delete fieldRefs.current[input.key];
      }}
    >
      {children}
    </div>
  );

  return (
    <div className={`${headerBottomMargin || 'mb-5'}`}>
      {!isHideHeader && (
        <HeaderIconTooltip
          heading="Custom Fields"
          spanTextStart="Vamos Managers can add additional fields in the"
          linkUrl="/admin/custom_fields"
          spanTextEnd="to capture data specific to your organization."
          linkText="Custom Fields page"
          place="right"
          iconName="icon_circle"
          id="customFields"
          tooltipId="custom-fields-tooltip"
          className={`pt-7 ${className || 'mb-8.5'}`}
          margins={margins}
          border={border}
          iconSize={iconSize}
          paddingBottom={paddingBottom}
          labelFontClassName={labelFontClassName}
        />
      )}
      <div
        className={`grid ${
          isSingleColForContacts ? 'grid-cols-1 w-full md:w-[calc(50%-1.19rem)]' : 'grid-cols-1'
        } gap-x-9.5 gap-y-4 mb-8`}
      >
        {customFields.map((input) => {
          let index;
          let fieldData;
          if (customFields) {
            fieldData = customFields.find((customField, i) => {
              index = i;
              return customField.label === input.label;
            });
          }

          // If in view-only mode, display fields as plain text
          if (isViewOnly) {
            return (
              <div key={input.key} className="grid grid-cols-2 gap-x-3 gap-y-6">
                <div className={`font-bold ${input.type === 'header' ? 'uppercase' : ''} ${input.type === 'header' && input.position !== 1 ? 'mt-2' : ''}`}>{input.label}</div>
                <div>{getFieldValue(input)}</div>
              </div>
            );
          }

          // Regular input fields
          switch (input.type) {
            case 'checkbox-group':
              return wrapWithRef(
                input,
                (
                  <Multiselect
                    inputHeight={`h-8 || ${inputFieldsHeight}`}
                    key={input.key}
                    label={input.label}
                    asteriskWidth="w-2"
                    spaceY="space-y-2"
                    labelBesideLabelClasses="w-full"
                    headerClassName="mb-0 text-sm font-medium text-vamos-black font-sans"
                    isRequired={input.required}
                    isLabelBeside={isLabelBeside}
                    options={input.values}
                    setValue={(values) => {
                      const selectedValues = values.filter((v) => v.selected).map((v) => v.value);
                      const updatedCustomFields = [...customFields];
                      updatedCustomFields[index].values = values;
                      updatedCustomFields[index].value = selectedValues;
                      setData((prev) => ({
                        ...prev,
                        custom_fields: [...updatedCustomFields],
                      }));
                      setCustomFieldsChanged(true);
                    }}
                    textSize="text-sm"
                    shouldNotCapitalize
                    errorMessage={errorMessage?.[`custom_fields.${input.label}`]}
                    errorMessageClass={errorMessageClass}
                    value={Array.isArray(fieldData.value) ? fieldData.value : []}
                  />
                ),
              );
            case 'textarea':
              return wrapWithRef(
                input,
                (
                  <TextArea
                    key={input.key}
                    label={input.label}
                    name={input.label}
                    placeholder="enter text"
                    asteriskWidth="w-2"
                    isRequired={input.required}
                    value={fieldData.value ?? ''}
                    setValue={(v) => {
                      const updatedCustomFields = [...customFields];
                      updatedCustomFields[index].value = v;
                      setData((prev) => ({
                        ...prev,
                        custom_fields: [...updatedCustomFields],
                      }));
                      setCustomFieldsChanged(true);
                    }}
                    isLabelBeside={isLabelBeside}
                    inputHeight={inputFieldsHeight || 'h-8'}
                    spaceY="space-y-0.5"
                    fontSize="text-sm"
                    className="p-2"
                    shouldNotCapitalize
                    errorMessage={errorMessage?.[`custom_fields.${input.label}`]}
                    errorMessageClass={errorMessageClass}
                    customWidth={customFieldMaxWidths.textarea}
                  />
                ),
              );
            case 'number':
              return wrapWithRef(
                input,
                (
                  <Number
                    key={input.key}
                    label={input.label}
                    name={input.label}
                    placeholder="enter a number"
                    asteriskWidth="w-2"
                    isRequired={input.required}
                    isValid={fieldData.value == null || fieldData.value === '' || parseFloat(fieldData.value) >= 0}
                    value={fieldData.value ?? ''}
                    setValue={(v) => {
                      const updatedCustomFields = [...customFields];
                      updatedCustomFields[index].value = v;
                      setData((prev) => ({
                        ...prev,
                        custom_fields: [...updatedCustomFields],
                      }));
                      setCustomFieldsChanged(true);
                      if (v !== '') {
                        clearFieldError?.(input.label);
                      }
                    }}
                    isLabelBeside={isLabelBeside}
                    inputHeight={inputFieldsHeight || 'h-8'}
                    spaceY="space-y-0.5"
                    fontSize="text-sm"
                    shouldNotCapitalize
                    errorMessage={errorMessage?.[`custom_fields.${input.label}`]}
                    errorMessageClass={errorMessageClass}
                    customWidth={customFieldMaxWidths.number || 'max-w-[14ch]'}
                    isNegativeAllowed={isNumberFieldNegativeAllowed}
                  />
                ),
              );
            case 'header':
              return (
                <Heading
                  key={input.key}
                  label={input.label}
                  labelFontClassName="text-left text-transform: capitalize font-medium text-xl"
                />
              );
            case 'select':
              return wrapWithRef(
                input,
                (
                  <CustomSelect
                    key={input.key}
                    label={input.label}
                    asteriskWidth="w-2"
                    spaceY="space-y-0.5"
                    labelBesideLabelClasses="w-full"
                    isRequired={input.required}
                    shouldShowClearOption={!input.required}
                    value={
                      input.values.find((value) => value.selected === true)
                        ? input.values.find((value) => value.selected === true).value
                        : ''
                    }
                    setValue={(v) => {
                      const updatedCustomFields = [...customFields];

                      // Clear selection
                      if (!v) {
                        updatedCustomFields[index].value = '';
                        updatedCustomFields[index].values = updatedCustomFields[index].values.map((opt) => ({
                          ...opt,
                          selected: false,
                        }));

                        setData((prev) => ({
                          ...prev,
                          custom_fields: [...updatedCustomFields],
                        }));
                        setCustomFieldsChanged(true);
                        return;
                      }

                      // Normal select behavior
                      updatedCustomFields[index].value = v.value;
                      updatedCustomFields[index].values = updatedCustomFields[index].values.map((opt) => ({
                        ...opt,
                        selected: opt.value === v.value,
                      }));

                      setData((prev) => ({
                        ...prev,
                        custom_fields: [...updatedCustomFields],
                      }));
                      setCustomFieldsChanged(true);
                    }}
                    options={input.values}
                    isLabelBeside={isLabelBeside}
                    inputHeight={inputHeightSelect}
                    labelFontSize="text-sm font-medium"
                    fontSize={14}
                    shouldNotCapitalize
                    errorMessage={errorMessage?.[`custom_fields.${input.label}`]}
                    errorMessageClass={errorMessageClass}
                    customWidth={customFieldMaxWidths.select || getComputedSelectCustomWidth(input)}
                  />
                ),
              );
            case 'date':
              return wrapWithRef(
                input,
                (
                  <DateSelectorV2
                    key={input.key}
                    label={input.label}
                    // Lowercased name keeps selectors consistent (e.g., touchdown).
                    name={input.label.toLowerCase()}
                    dataTestId={`custom-date-${input.label.toLowerCase().replace(/\s+/g, '-')}`}
                    placeholder="select a date"
                    inputStyle={{ padding: '0 12px 0 12px', background: 'transparent' }}
                    asteriskWidth="w-2"
                    isRequired={input.required}
                    value={fieldData.value ?? ''}
                    setValue={(v) => {
                      const updatedCustomFields = [...customFields];
                      updatedCustomFields[index].value = v;
                      setData((prev) => ({
                        ...prev,
                        custom_fields: [...updatedCustomFields],
                      }));
                      setCustomFieldsChanged(true);
                    }}
                    isLabelBeside={isLabelBeside}
                    inputHeight={inputFieldsHeight || 'h-8'}
                    spaceY="space-y-0.5"
                    fontSize="text-sm"
                    iconRight={iconRight}
                    iconBottom={iconBottom}
                    errorMessage={errorMessage?.[`custom_fields.${input.label}`]}
                    errorMessageClass={errorMessageClass}
                    errorMessageZIndexClass="z-0"
                    customWidth={customFieldMaxWidths.date || 'max-w-[14ch]'}
                  />
                ),
                'w-full text-left mb-4',
              );
            case 'text':
              return wrapWithRef(
                input,
                (
                  <TextInput
                    key={input.key}
                    label={input.label}
                    name={input.label}
                    placeholder="enter text"
                    asteriskWidth="w-2"
                    isRequired={input.required}
                    value={fieldData.value ?? ''}
                    setValue={(v) => {
                      const updatedCustomFields = [...customFields];
                      updatedCustomFields[index].value = v;
                      setData((prev) => ({
                        ...prev,
                        custom_fields: [...updatedCustomFields],
                      }));
                      setCustomFieldsChanged(true);
                    }}
                    isLabelBeside={isLabelBeside}
                    inputHeight={inputFieldsHeight || 'h-8'}
                    spaceY="space-y-0.5"
                    fontSize="text-sm"
                    shouldNotCapitalize
                    errorMessage={errorMessage?.[`custom_fields.${input.label}`]}
                    errorMessageClass={errorMessageClass}
                    customWidth={customFieldMaxWidths.text}
                  />
                ),
              );
            default:
              return <></>;
          }
        })}
      </div>
    </div>
  );
};

CustomFieldsForm.propTypes = {
  customFields: PropTypes.array,
  setData: PropTypes.func,
  isLabelBeside: PropTypes.bool,
  isViewOnly: PropTypes.bool, // New prop for view-only mode
  iconSize: PropTypes.string,
  labelFontClassName: PropTypes.string,
  border: PropTypes.string,
  headerBottomMargin: PropTypes.string,
  margins: PropTypes.string,
  paddingBottom: PropTypes.string,
  isHideHeader: PropTypes.bool,
  isSingleColForContacts: PropTypes.bool,
  inputFieldsHeight: PropTypes.string,
  inputHeightSelect: PropTypes.number,
  iconRight: PropTypes.number,
  iconBottom: PropTypes.number,
  errorMessage: PropTypes.object,
  errorMessageClass: PropTypes.node,
  className: PropTypes.string,
  setCustomFieldsChanged: PropTypes.func,
  shouldScrollToError: PropTypes.bool,
  isNumberFieldNegativeAllowed: PropTypes.bool,
  clearFieldError: PropTypes.func,
  customFieldMaxWidths: PropTypes.shape({
    number: PropTypes.string,
    date: PropTypes.string,
    select: PropTypes.string,
    checkboxGroup: PropTypes.string,
    text: PropTypes.string,
    textarea: PropTypes.string,
  }),
};

export default CustomFieldsForm;
